import { Component, OnInit, computed, inject, linkedSignal, signal } from "@angular/core";
import { FlexLayoutModule, FlexModule } from "@ngbracket/ngx-layout";
import { AggregateHelperService, AggregateService } from "@tylertech/aggregates-module";
import { NavigationService } from "@tylertech/core-module";
import { ForgeIconButtonModule, ForgeIconModule, ForgeSkeletonModule, ForgeTooltipModule, ForgeViewModule, ForgeViewSwitcherModule } from "@tylertech/forge-angular";
import { ExecutionResult, IAggregate, IMetadataAction } from "@tylertech/tyler-api-types";
import { IWidget } from "./iWidget";
import { WidgetComponent } from "./widget.component";

interface WidgetAggregate extends IAggregate
{
    Url: string;
    Icon: string;
    Color: string;
    ClickEvent: string;
    TextAlign: string;
    MetadataAction: IMetadataAction;
    SecondaryText: string;
}

@Component({
    selector: "widget-bar",
    imports: [
        FlexModule,
        FlexLayoutModule,
        ForgeSkeletonModule,
        ForgeIconButtonModule,
        ForgeIconModule,
        ForgeTooltipModule,
        ForgeViewSwitcherModule,
        ForgeViewModule,
        WidgetComponent,
    ],
    templateUrl: "./widgetBar.tmpl.html",
    styleUrls: ["./widgetBar.component.scss"],
})
export class WidgetBarComponent implements OnInit
{
    private $aggregateService = inject(AggregateService);
    private $aggregateHelperService = inject(AggregateHelperService);
    private readonly $navigationService = inject(NavigationService);

    readonly pageSize: number = 4;

    readonly pages = signal<Array<Array<IWidget>>>([]);
    readonly pageIndex = signal(0);
    readonly widgetsLoaded = signal(false);

    readonly hasPreviousPage = computed(() => this.pageIndex() > 0);

    readonly hasNextPage = computed(() => this.pageIndex() + 1 < this.pages().length);

    readonly shownWidgets = linkedSignal(() => this.pages()[this.pageIndex()]);

    ngOnInit(): void
    {
        this.fetchTileAggregates()
            .then(widgets =>
            {
                let index = 0;
                let pageIndex = 0;
                const pages = [];

                for (const widget of widgets)
                {
                    if (!pages[pageIndex])
                    {
                        pages[pageIndex] = [];
                    }

                    pages[pageIndex].push(widget);
                    index++;

                    if (index >= this.pageSize)
                    {
                        index = 0;
                        pageIndex++;
                    }
                }

                this.pages.set(pages);
            })
            .finally(() =>
            {
                this.widgetsLoaded.set(true);
            });
    };

    goToPreviousPage(): void
    {
        this.pageIndex.set(this.pageIndex() - 1);
    };

    goToNextPage(): void
    {
        this.pageIndex.set(this.pageIndex() + 1);
    }

    private fetchTileAggregates(): Promise<IWidget[]>
    {
        return this.$aggregateService.GetWidgetAggregates()
            .then((result: ExecutionResult<Array<WidgetAggregate>>) =>
            {
                const widgets = result.Result ?? [];
                return widgets.filter(x => !!x)
                    .map(x => this.convertToWidget(x));
            });
    }

    private convertToWidget(x: WidgetAggregate): IWidget
    {
        const widget: IWidget = {
            title: x.Name,
            count: x.Count,
            icon: x.Icon,
            color: x.Color,
            url: x.Url,
            textAlign: x.TextAlign,
            metadataAction: x.MetadataAction,
            secondaryText: x.SecondaryText,
        };

        if (x.Url && x.Url.substring(0, 1) === "/")
        {
            widget.url = this.$navigationService.getLinkToApp(x.Url) as string;
        }

        const intent = this.$aggregateHelperService.getDataElementByLabel("intent", x.AggregateCardDataElements);
        if (intent)
        {
            widget.mime = intent.Type;
            widget.action = intent.Value;
        }

        return widget;
    }
}
